<template>
  <div class="pb-home-content">
    <!-- <pre>{{ sortOptions }}</pre> -->
    <DataViewHeader
      v-if="showFilters || showSearch || showRefresh"
      class="pb-profiles-list-header"
      :refresh="showRefresh"
      :readonly="loading"
      @refresh="load"
    >
      <div class="pb-profiles-list-header-content">
        <div class="pb-profiles-list-header-content-left">
          <ProfileListFilters v-if="showFilters" />
          <div
            v-if="showSearch"
            class="pb-profiles-list-search"
          >
            <PbInput
              v-model="tempName"
              :label="translate('generic.name')"
              class="pb-profile-list-name-search"
              dynamic
              :disabled="loading"
              @keypress.enter="search.applySearch"
            />
            <BrandsField
              v-model="tempBrand"
              allow-all
              :disabled="loading"
              class="pb-profile-list-brand-search"
            />
          </div>
        </div>
        <div
          v-if="showSearch"
          class="pb-profiles-list-header-content-right"
        >
          <ControlButton v-bind="searchProps.submit" />
        </div>
      </div>
    </DataViewHeader>

    <UiTable
      v-if="profiles.length || loading"
      id="profiles-list"
      :is-loading="loading"
      :loading-row-count="placeholderCount"
      class="pb-profiles-list-table"
    >
      <template #header>
        <DataViewSortHeader
          field="name"
          class="pb-column-header pb-profiles-list-column-name"
          v-bind="sortProps.header"
        >
          {{ translate('generic.name') }}
        </DataViewSortHeader>
        <DataViewSortHeader
          v-if="columnBrand"
          field="brand"
          class="pb-column-header pb-profiles-list-column-brand"
          v-bind="sortProps.header"
        >
          {{ translate('generic.brand') }}
        </DataViewSortHeader>
        <TableCell
          v-if="columnOwner"
          class="pb-column-header pb-profiles-list-column-owner"
          head
          left
        >
          {{ translate('generic.owner') }}
        </TableCell>
        <DataViewSortHeader
          v-if="columnUpdated"
          field="updatedDate"
          class="pb-column-header pb-profiles-list-column-updated"
          v-bind="sortProps.header"
        >
          {{ translate('generic.updatedDate') }}
        </DataViewSortHeader>
        <TableCell
          v-if="columnWww"
          class="pb-column-header pb-profiles-list-column-icons"
          head
          center
        >
          <UiIcon
            v-tooltip="{ text: translate('pb2.profilePropertiesView.profileWorksWithWolfmixLabel') }"
            icon="www"
            color="muted"
            class="pb-icon-cell-item"
            size="medium"
          />
        </TableCell>
        <TableCell
          v-if="columnOfficial"
          class="pb-column-header pb-profiles-list-column-icons"
          head
          center
        >
          <UiIcon
            v-tooltip="{ text: translate('generic.official') }"
            icon="certified"
            color="muted"
            class="pb-icon-cell-item"
          />
        </TableCell>
        <slot
          name="header"
          v-bind="{
            trashFilterEnabled,
            officialFilterEnabled,
            mineFilterEnabled,
            allFilterEnabled,
          }"
        />
      </template>

      <TableRow
        v-for="item in profiles"
        :key="item.uid"
        v-bind="getItemProps(item)"
      >
        <template #default="cellProps">
          <TableCell
            v-bind="cellProps"
            class="pb-profiles-list-column-name"
            left
            strong
            :label="item.uid"
          >
            {{ item.name }}
          </TableCell>
          <TableCell
            v-if="columnBrand"
            v-bind="cellProps"
            class="pb-profiles-list-column-brand"
            muted
            :label="item.uid"
          >
            {{ item.brand }}
          </TableCell>
          <TableCell
            v-if="columnOwner"
            v-bind="cellProps"
            class="pb-profiles-list-column-owner"
            muted
            :label="item.uid"
          >
            <template v-if="officialFilterEnabled">
              {{ translate('generic.official') }}
            </template>
            <template v-else>
              {{ (item.owner && item.owner.displayName) || translate('generic.noOwner') }}
            </template>
          </TableCell>
          <TableCell
            v-if="columnUpdated"
            v-bind="cellProps"
            class="pb-profiles-list-column-updated"
            muted
            :label="item.uid"
          >
            {{ item.formattedUpdatedDate }}
          </TableCell>
          <TableCell
            v-if="columnWww"
            v-bind="cellProps"
            class="pb-profiles-list-column-icons pb-icon-cell"
            center
            :label="item.uid"
          >
            <UiIcon
              v-if="item.worksWithWolfmix"
              v-tooltip="{ text: translate('pb2.profilePropertiesView.profileWorksWithWolfmixLabel') }"
              icon="www"
              size="medium"
              class="pb-icon-cell-item pb-works-with-wolfmix-icon"
            />
          </TableCell>
          <TableCell
            v-if="columnOfficial"
            v-bind="cellProps"
            class="pb-profiles-list-column-icons pb-icon-cell"
            center
            :label="item.uid"
          >
            <UiIcon
              v-if="item.isCertificated"
              v-tooltip="{ text: translate('generic.official') }"
              icon="certified"
              color="success"
              class="pb-icon-cell-item"
            />
          </TableCell>
          <slot
            name="default"
            v-bind="{
              item,
              trashFilterEnabled,
              officialFilterEnabled,
              mineFilterEnabled,
              allFilterEnabled,
              cellProps,
            }"
          />
        </template>
      </TableRow>
    </UiTable>
    <NoItemFound v-else>
      <slot
        name="no-result"
        v-bind="{
          trashFilterEnabled,
          officialFilterEnabled,
          mineFilterEnabled,
          allFilterEnabled,
        }"
      >
        <h2>{{ noItemFoundMessage ?? translate('pb2.homeView.noProfileFound') }}</h2>
      </slot>
    </NoItemFound>

    <DataViewFooter
      pagination
      goto
      v-bind="paginationProps.footer"
    >
      <SelectField
        class="pb-dataview-limit"
        :aria-label="translate('pb2.homeView.limitLabel')"
        :size="SIZE_SMALL"
        v-bind="paginationProps.limit"
      />
      <UiTypo>
        {{ paginationText }}
      </UiTypo>
    </DataViewFooter>
  </div>
</template>

<script setup>
import {
  computed,
  watch,
} from 'vue'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import DataViewHeader from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewHeader.vue'
import DataViewFooter from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewFooter.vue'
import DataViewSortHeader from '@nicolaudie-group/ng-ui-kit/components/DataView/DataViewSortHeader.vue'
import NoItemFound from '@nicolaudie-group/ng-ui-kit/components/NoItemFound/NoItemFound.vue'
import TableRow from '@nicolaudie-group/ng-ui-kit/components/Table/TableRow.vue'
import TableCell from '@nicolaudie-group/ng-ui-kit/components/Table/TableCell.vue'
import UiTable from '@nicolaudie-group/ng-ui-kit/components/Table/UiTable.vue'
import UiTypo from '@nicolaudie-group/ng-ui-kit/components/UiTypo/UiTypo.vue'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import useTranslate from '@/composables/useTranslate'
import SelectField from '@/components/SelectField/SelectField.vue'
import BrandsField from '@/components/BrandsField/BrandsField.vue'
import PbInput from '@/components/PbInput.vue'
import { SIZE_SMALL } from '@nicolaudie-group/ng-ui-kit/components/Listbox/symbols'

import { useProfileListInject } from '@/composables/useProfileList'
import ProfileListFilters from './ProfileListFilters.vue'

// const emit = defineEmits(['input'])

const props = defineProps({
  showFilters: { type: Boolean, default: false },
  showSearch: { type: Boolean, default: false },
  showRefresh: { type: Boolean, default: false },
  /**
   * Flag to enable the "brand" column
   */
  columnBrand: { type: Boolean, default: false },
  /**
   * Flag to enable the "owner" column
   */
  columnOwner: { type: Boolean, default: false },
  /**
   * Flag to enable the "updated date" column
   */
  columnUpdated: { type: Boolean, default: false },
  /**
   * Flag to enable the "works with wolfmix" column
   */
  columnWww: { type: Boolean, default: false },
  /**
   * Flag to enable the "official" column
   */
  columnOfficial: { type: Boolean, default: false },
  /**
   *
   */
  link: { type: Boolean, default: false },

  value: { type: [Object, String, Number], default: undefined },

  label: { type: Boolean, default: false },

  allowEmpty: { type: Boolean, default: false },

  noItemFoundMessage: { type: String, default: undefined },
})

// const { translate, translationsLoaded } = useTranslate()
const { translate } = useTranslate()

const {
  filter,
  load,
  loading,
  pagination,
  requestParams,
  search,
  sort,
  profiles,
} = useProfileListInject()

const {
  tempName,
  tempBrand,
  searchProps,
} = search

const {
  paginationProps,
  paginationText,
} = pagination

const {
  sortProps,
} = sort

const {
  allFilterEnabled,
  mineFilterEnabled,
  trashFilterEnabled,
  officialFilterEnabled,
} = filter

/**
 * Number of placeholders to display while fetching data
 */
const placeholderCount = computed(() => (
  pagination.itemsInPage.value > 0 ? pagination.itemsInPage.value : 10
))

watch(requestParams, () => {
  if (props.allowEmpty || (tempName.value && tempBrand.value)) {
    load()
  }
}, {
  // Load the profiles on initialization
  immediate: true,
  /*
  Wait for the component to be ready (rendered, or in this case what we want is unmounted)
  before loading the profiles, so that it's not triggered needlessly when
  navigating away because the queries changes to undefined
  */
  flush: 'post',
})

function getItemProps(item) {
  if (props.link) {
    return {
      to: item.link,
    }
  }
  return {
    active: item.uid === props.value,
    activeClass: 'pb-profiles-list-active-row',
    // onSelect: () => emit('input', item.uid),
  }
}
</script>

<style lang="scss" src="./ProfileList.scss" />
